/**
 * Utilities for converting between JSON and XML formats.
 *
 * This module provides functions for converting JavaScript objects to XML
 * and back, enabling seamless integration with XML-based systems without
 * the need to maintain separate data models or serialization methods.
 */

// type JsonValue = string | number | boolean | null | undefined;
// type JsonObject = Record<string, JsonValue | JsonObject | JsonArray>;
// type JsonArray = Array<JsonValue | JsonObject | JsonArray>;
type JsonValue = string | number | boolean | null | undefined | JsonObject | JsonArray;

interface JsonObject {
  [key: string]: JsonValue;
}

interface JsonArray extends Array<JsonValue> {}

// Marker constants for special values
const NULL_MARKER = "__NULL__"; // Used to encode null values in XML
const EMPTY_ARRAY_MARKER = "__EMPTY_ARRAY__"; // Used to encode empty arrays in XML
const EMPTY_OBJECT_MARKER = "__EMPTY_OBJECT__"; // Used to encode empty objects in XML

/**
 * Checks if an object is an empty object (not array).
 */
function isEmptyObject(obj: any): boolean {
  return typeof obj === "object" && obj !== null && !Array.isArray(obj) && Object.keys(obj).length === 0;
}

/**
 * Checks if a value is an empty array.
 */
function isEmptyArray(arr: any): boolean {
  return Array.isArray(arr) && arr.length === 0;
}

/**
 * Checks if an XML node is a marker node (e.g., for null/empty array/object).
 */
function isMarkerNode(node: Element, marker: string): boolean {
  return node.nodeName === marker || (node.children.length === 1 && node.children[0].nodeName === marker);
}

/**
 * Checks if a value is a primitive type that can be directly converted to a string.
 */
function isPrimitive(value: any): boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    value === undefined
  );
}

/**
 * Escapes invalid characters in XML attribute names.
 */
function escapeXmlAttrName(name: string): string {
  let result = name.replace(/\s/g, "_").replace(/-/g, "_");
  if (result && !/^[a-zA-Z_]/.test(result)) {
    result = `_${result}`;
  }
  return result;
}

/**
 * Escapes XML special characters in text nodes.
 */
function escapeXmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/'/g, "&apos;")
    .replace(/"/g, "&quot;");
}

/**
 * Converts a JavaScript object or array to an XML string.
 * Handles primitives, arrays, objects, null, empty objects/arrays, and special characters.
 */
export function jsonToXml(
  data: JsonObject | JsonArray,
  rootName: string = "root",
  namespace: string | null = null
): string {
  const nsAttribute = namespace ? ` xmlns="${namespace}"` : "";
  let xml = `<${rootName}${nsAttribute}>`;

  /**
   * Recursively adds a key/value node to the XML string.
   */
  function addNode(key: string, value: any): string {
    // Handle primitives (string, number, boolean, null, undefined)
    if (isPrimitive(value)) {
      if (value === null) {
        // Encode null as a marker node
        return `<${key}><${NULL_MARKER} /></${key}>`;
      }
      if (value === undefined) {
        // Omit undefined values from XML
        return "";
      }
      // Convert booleans to 'true'/'false', escape text for others
      const text = typeof value === "boolean" ? String(value).toLowerCase() : escapeXmlText(String(value));
      return `<${key}>${text}</${key}>`;
    }
    // Handle empty arrays
    if (isEmptyArray(value)) {
      return `<${key}><${EMPTY_ARRAY_MARKER} /></${key}>`;
    }
    // Handle arrays
    if (Array.isArray(value)) {
      return `<${key}>${value.map((item) => addNode("item", item)).join("")}</${key}>`;
    }
    // Handle empty objects
    if (isEmptyObject(value)) {
      return `<${key}><${EMPTY_OBJECT_MARKER} /></${key}>`;
    }
    // Handle objects
    if (typeof value === "object") {
      return `<${key}>${Object.entries(value)
        .map(([k, v]) => addNode(escapeXmlAttrName(k), v))
        .join("")}</${key}>`;
    }
    // Fallback for any other type
    return `<${key}>${escapeXmlText(String(value))}</${key}>`;
  }

  // Handle root data (array or object)
  if (isEmptyArray(data)) {
    xml += `<${EMPTY_ARRAY_MARKER} />`;
  } else if (Array.isArray(data)) {
    xml += data.map((item) => addNode("item", item)).join("");
  } else {
    xml += Object.entries(data)
      .map(([k, v]) => addNode(escapeXmlAttrName(k), v))
      .join("");
  }
  xml += `</${rootName}>`;
  return xml;
}

/**
 * Converts an XML string to a JavaScript object, array, or primitive value.
 * Handles special marker nodes for null, empty arrays, and empty objects.
 */
export function xmlToJson(xmlString: string): JsonValue | JsonObject | JsonArray {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, "application/xml");
  const parserError = xmlDoc.getElementsByTagName("parsererror");
  if (parserError.length > 0) {
    throw new Error("Invalid XML: " + parserError[0].textContent);
  }

  /**
   * Recursively processes an XML node and reconstructs the corresponding JS value.
   */
  function processNode(node: Element): any {
    // Handle marker nodes for null, empty array, empty object
    if (isMarkerNode(node, NULL_MARKER)) return null;
    if (isMarkerNode(node, EMPTY_ARRAY_MARKER)) return [];
    if (isMarkerNode(node, EMPTY_OBJECT_MARKER)) return {};

    // If no children, treat as primitive
    if (node.children.length === 0) {
      if (node.textContent !== null) {
        const text = node.textContent.trim();
        // Convert to boolean/number if possible, else return as string
        if (text === "true") return true;
        if (text === "false") return false;
        if (!isNaN(Number(text)) && text !== "") return Number(text);
        return text;
      }
      return null;
    }

    // Handle arrays (all children named 'item')
    const childTagNames = Array.from(node.children).map((c) => c.nodeName);
    if (childTagNames.every((n) => n === "item")) {
      return Array.from(node.children).map((c) => processNode(c));
    }

    // Handle objects (children with different names)
    const obj: any = {};
    for (const child of Array.from(node.children)) {
      const val = processNode(child);
      if (obj[child.nodeName] !== undefined) {
        // Multiple children with same name become an array
        if (!Array.isArray(obj[child.nodeName])) {
          obj[child.nodeName] = [obj[child.nodeName]];
        }
        obj[child.nodeName].push(val);
      } else {
        obj[child.nodeName] = val;
      }
    }
    return obj;
  }
  return processNode(xmlDoc.documentElement);
}

/**
 * Convert a JavaScript object or array to an XML string with formatting options.
 *
 * @param data - Object or array to convert to XML
 * @param rootName - Name for the root element of the XML tree
 * @param namespace - Optional XML namespace
 * @param prettyPrint - Whether to format the XML with indentation
 * @param xmlDeclaration - Whether to include the XML declaration
 * @param encoding - Character encoding for the XML
 * @returns XML string representing the data
 */
export function jsonToXmlString(
  data: JsonObject | JsonArray,
  rootName: string = "root",
  namespace: string | null = null,
  prettyPrint: boolean = true,
  xmlDeclaration: boolean = true,
  encoding: string = "UTF-8"
): string {
  let result = "";

  // Add XML declaration if requested
  if (xmlDeclaration) {
    result += `<?xml version="1.0" encoding="${encoding}"?>\n`;
  }

  // Convert to XML
  let xmlContent = jsonToXml(data, rootName, namespace);

  // Format if requested
  if (prettyPrint) {
    xmlContent = formatXml(xmlContent);
  }

  return result + xmlContent;
}

/**
 * Convert an XML string to a JavaScript object, array, or primitive value.
 *
 * @param xmlString - XML string to convert
 * @returns JavaScript object, array, or primitive value representing the XML content
 */
export function xmlStringToJson(xmlString: string): JsonValue | JsonObject | JsonArray {
  return xmlToJson(xmlString);
}

/**
 * Format an XML string with indentation.
 *
 * @param xml - The XML string to format
 * @returns The formatted XML string
 */
function formatXml(xml: string): string {
  let formatted = "";
  let indent = "";
  const tab = "  "; // 2 spaces for indentation

  // Extract XML declaration if it exists
  const xmlDeclaration = xml.match(/^<\?xml[^>]*\?>/);
  const xmlWithoutDeclaration = xml.replace(/^<\?xml[^>]*\?>\s*/, "");

  // Remove any existing angle brackets from the start and end of the remaining XML
  const cleanXml = xmlWithoutDeclaration.replace(/^<+/, "").replace(/>+$/, "");

  // Add XML declaration if it exists
  if (xmlDeclaration) {
    formatted += xmlDeclaration[0] + "\n";
  }

  cleanXml.split(/>\s*</).forEach((node) => {
    if (node.match(/^\/\w/)) {
      // End of tag
      indent = indent.substring(tab.length);
    }

    formatted += indent + "<" + node + ">\n";

    if (node.match(/^<?\w[^>]*[^/]$/) && !node.startsWith("?")) {
      // Start of tag and not self-closing
      indent += tab;
    }
  });

  // Clean up the XML formatting a bit
  return formatted
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/<>\s*|\s*<\/>/g, "")
    .replace(/><\//g, ">\n</")
    .trim();
}
